import React from 'react';
import { formatMoney } from '../../lib/formatMoney';

// Invoice-style summary. Renders breakdown.lines generically — future line
// types (credit, gift_card) appear with no changes here. Savings amounts
// arrive negative from the server; rendered as "-$X.XX".
const OrderSummary = ({ breakdown, currency, trial }) => {
    if (!breakdown) return null;
    const lines = breakdown.lines || [];
    // Customer-balance types apply AFTER the invoice total (due_today =
    // total - credit), so they render between Total and Due today.
    const POST_TOTAL_TYPES = ['credit', 'gift_card'];
    const preTotalLines = lines.filter((l) => !POST_TOTAL_TYPES.includes(l.type));
    const postTotalLines = lines.filter((l) => POST_TOTAL_TYPES.includes(l.type));
    const hasCoupon = lines.some((l) => l.type === 'coupon');
    const isTrial = Boolean(trial);
    const trialStart = trial?.first_charge_at ? new Date(trial.first_charge_at) : null;

    return (
        <div className="rounded-xl border border-white/10 bg-[#0b0e14]/60 p-5">
            <h2 className="font-semibold border-b border-white/10 pb-3 mb-3">Order Summary</h2>
            <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                    <dt className="text-neutral-400">Subtotal:</dt>
                    <dd>{formatMoney(breakdown.subtotal, currency)}</dd>
                </div>
                {preTotalLines.map((line, i) => (
                    <div key={i} className={`flex justify-between ${line.type === 'coupon' ? 'text-emerald-300' : 'text-neutral-300'}`}>
                        <dt>{line.label}:</dt>
                        <dd>{`-${formatMoney(Math.abs(line.amount), currency)}`}</dd>
                    </div>
                ))}
                {!hasCoupon && !isTrial && (
                    <div className="flex justify-between text-neutral-600">
                        <dt>Coupon Saving:</dt>
                        <dd>{`-${formatMoney(0, currency)}`}</dd>
                    </div>
                )}
                <div className={`flex justify-between ${breakdown.tax > 0 ? '' : 'text-neutral-600'}`}>
                    <dt>Tax:</dt>
                    <dd>{formatMoney(breakdown.tax, currency)}</dd>
                </div>
            </dl>
            <div className="border-t border-white/10 mt-3 pt-3 space-y-1">
                <div className="flex justify-between text-sm">
                    <span className="text-neutral-400">Total:</span>
                    <span className="font-semibold">
                        {formatMoney(breakdown.total, currency)}
                        {isTrial && <span className="text-neutral-400 font-normal"> /mo after trial</span>}
                    </span>
                </div>
                {postTotalLines.map((line, i) => (
                    <div key={i} className="flex justify-between text-sm text-emerald-300">
                        <span>{line.label}:</span>
                        <span>{`-${formatMoney(Math.abs(line.amount), currency)}`}</span>
                    </div>
                ))}
                <div className="flex justify-between items-baseline">
                    <span className="font-semibold">Due today:</span>
                    <span className="text-xl font-bold">{formatMoney(breakdown.due_today, currency)}</span>
                </div>
                {isTrial && (
                    <p className="text-xs text-neutral-400 pt-1">
                        then {formatMoney(trial.amount, trial.currency || currency)}/mo starting {trialStart ? trialStart.toLocaleDateString() : 'after your trial'}
                    </p>
                )}
            </div>
        </div>
    );
};

export default OrderSummary;
